import { Dialog, IconButton, useMediaQuery } from "@mui/material";
import { useDeviceTableSettings } from "./DevicesPage";
import { EnhancedTable, Filter as TableFilter, RemoteFilterViews } from "@mb-pro-ui/components/table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { isEqual } from "lodash-es";
import { useMutation } from "react-query";

import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";

import { FilteredDeviceListItem } from "../utils/types";
import { DeviceFilter } from "../utils/ServerCommunicaton";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { useApi } from "../utils/ApiProvider";
import { UnitPermissionLike } from "./UserUnitPermissionsSubpage";

type UnitPermissionSelectWithFullSearchDialogProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (value: UnitPermissionLike, element: HTMLElement) => void;
  currentPermissions: UnitPermissionLike[];
};
const limit = 1000;

export const UnitPermissionSelectWithFullSearchDialog = ({ open, onClose, onSelect, currentPermissions }: UnitPermissionSelectWithFullSearchDialogProps) => {
  const [filter, setFilter] = useState<DeviceFilter | null>(null);

  const api = useApi();
  const narrow = useMediaQuery("(max-width:1000px)", { noSsr: true });

  const { columns, columnsNarrow, tableFilters, getQueryFilter } = useDeviceTableSettings();

  // TODO: többi előfordulható hiba
  const { errorHandler: onError } = useErrorHandler([]);

  const listQuery = useCallback(
    (filter: DeviceFilter) =>
      api<FilteredDeviceListItem[]>("/units", {
        method: "post",
        body: JSON.stringify(filter),
      }),
    [api]
  );
  const { mutate: getList, data, status: queryStatus } = useMutation({ mutationFn: listQuery, onError });
  const list = useMemo(() => {
    if (!data) {
      return data;
    }
    const unitids = new Set<number>();
    currentPermissions.forEach((permission) => unitids.add(permission.unitid));
    return data.filter((item) => !unitids.has(item.unitid));
  }, [currentPermissions, data]);

  const filterChanged = useCallback(
    (filter: TableFilter[]) => {
      setFilter((old) => {
        const newFilter = getQueryFilter(filter);
        return isEqual(old, newFilter) ? old : newFilter;
      });
    },
    [getQueryFilter]
  );

  useEffect(() => {
    if (!filter) {
      return;
    }
    getList(filter);
  }, [filter, getList]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen sx={{ m: { xs: 1, sm: 2, md: 3 } }}>
      <EnhancedTable
        columns={narrow ? columnsNarrow : columns}
        data={list ?? []}
        showResultCount
        queryStatus={queryStatus}
        overscanCount={5}
        rowHeight={narrow ? 52 : undefined}
        filters={(opts) => <RemoteFilterViews {...opts} tableFilters={tableFilters} onFilterChange={filterChanged} />}
        limit={limit}
        sx={{ borderRadius: 0 }}
        actionCellWidth={52}
        actionPosition="left"
        action={({ original }) => (
          <IconButton
            size="small"
            sx={{ color: (theme) => theme.palette.secondary.main }}
            onClick={(event) =>
              onSelect(
                {
                  unitid: original.unitid,
                  company: original.company_name,
                  hwinfo: [original.hwtype, original.hwver].filter((i) => !!i).join("."),
                  edited: false,
                },
                event.currentTarget
              )
            }
          >
            <AddIcon />
          </IconButton>
        )}
        postfix={
          <IconButton
            size="small"
            sx={{
              color: "primary.contrastText",
            }}
            onClick={onClose}
          >
            <ClearIcon />
          </IconButton>
        }
      />
    </Dialog>
  );
};
